import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import {observer} from "mobx-react-lite";
import {useChatContext} from "../context/ChatContext";
import type {ChatSession} from "../types/chatTypes";
import moment from "moment";
import EditSquareIcon from '../../../components/icons/svg/EditSquareIcon.svg?react';
import SidebarIcon from '../../../components/icons/svg/SidebarIcon.svg?react';
import ChartBarSquareIcon from '~/icons/svg/ChartBarSquareIcon.svg?react';
import ChatSquareIcon from '~/icons/svg/ChatSquareIcon.svg?react';
import {ActionsMenu} from './ActionsMenu/ActionsMenu';
import {useRenameSessionMutation} from '../hooks/useRenameSessionMutation';
import {COLORS} from "~/style/colors-ts";
import {notificationStore} from "../../../stores/init";

type SessionGroup = {
    label: string;
    sessions: ChatSession[];
};

const SIDEBAR_SESSIONS_SCROLL_ID = 'chat-sidebar-sessions-scroll';

const groupSessionsByDate = (sessions: ChatSession[]): SessionGroup[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: Map<string, ChatSession[]> = new Map();

    // Sort sessions by updated_at descending (most recent first)
    const sortedSessions = [...sessions].sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime());
    for (const session of sortedSessions) {
        const sessionDate = new Date(session.updated_at.getFullYear(), session.updated_at.getMonth(), session.updated_at.getDate());

        let groupLabel: string;
        if (sessionDate.getTime() === today.getTime()) {
            groupLabel = 'Today';
        } else if (sessionDate.getTime() === yesterday.getTime()) {
            groupLabel = 'Yesterday';
        } else {
            // Format as "Monday, 23 Jan 2023" (in appropriate locale)
            const m = moment(session.updated_at);
            groupLabel = `${m.format('dddd')}, ${m.format('ll')}`
        }
        if (!groups.has(groupLabel)) {
            groups.set(groupLabel, []);
        }
        groups.get(groupLabel)!.push(session);
    }

    // Convert map to array while preserving order
    return Array.from(groups.entries()).map(([label, sessions]) => ({
        label,
        sessions,
    }));
};

export const Sidebar: React.FC = observer(() => {
    const {
        sessions,
        currentSession,
        selectSession,
        startNewChat,
        updateSessionName,
        loadMoreSessions,
        hasMoreSessions,
        sessionsPending,
        isRestoringSession,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        isStreaming,
    } = useChatContext();
    const groupedSessions = useMemo(() => groupSessionsByDate(sessions), [sessions]);
    const onNewChat = !currentSession && !isRestoringSession;
    const [renameSessionName, setRenameSessionName] = useState('');
    const [renameSessionId, setRenameSessionId] = useState<string | null>('');
    const {mutate: renameSession} = useRenameSessionMutation();

    function handleSessionIdSelection(sessionId: string, sessionName: string) {
        setRenameSessionName(sessionName);
        setRenameSessionId(sessionId);
    }

    const renameSessionNameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (!renameSessionId) return;
        const timeout = setTimeout(() => {
            renameSessionNameRef.current?.focus();
            renameSessionNameRef.current?.select();
        }, 100);
        return () => clearTimeout(timeout);
    }, [renameSessionId]);

    function handleRenameSessionSubmit(appName: string) {
        if (!renameSessionId) return;
        if (!renameSessionName) return;
        const sessionId = renameSessionId;
        renameSession(
            {sessionId, displayName: renameSessionName, appName},
            {
                onSuccess: () => {
                    updateSessionName(sessionId, renameSessionName);
                    setRenameSessionId(null);
                    setRenameSessionName('');
                },
                onError: () => {
                    setRenameSessionId(null);
                    setRenameSessionName('');
                    notificationStore.show("Could not rename chat. Please try again.", {
                        severity: "error",
                        id: `chat-rename-error-${sessionId}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    }

    function handleRenameSessionCancel() {
        setRenameSessionId(null);
        setRenameSessionName('');
    }

    function handleNewChat() {
        startNewChat();
    }

    const collapsedIconSx = isSidebarCollapsed ? {alignSelf: 'center', mr: '0 !important'} : undefined;
    const collapsedButtonSx = isSidebarCollapsed
        ? {justifyContent: 'center', px: 1.5}
        : {flex: 1, minWidth: 0};

    const collapseButton = (
        <Tooltip title={isSidebarCollapsed ? 'Expand' : 'Collapse'} placement="right">
            {/* <IconButton
                size="small"

                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                sx={isSidebarCollapsed ? {alignSelf: 'center'} : {mr: 0.5, flexShrink: 0}}
            > */}
            <ListItemIcon sx={collapsedIconSx} onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                <ListItemButton sx={!isSidebarCollapsed ? {pr: '2px !important', '&:hover': {bgcolor: 'transparent !important'}, '&:focus': {bgcolor: 'transparent !important'}} : {}}>
                    <SidebarIcon width={18} height={18}/>
                </ListItemButton>
            </ListItemIcon>

            {/* </IconButton> */}
        </Tooltip>
    );

    return <div className={`sidebar${isSidebarCollapsed ? ' sidebar--collapsed' : ''}`}>
        {/* Header */}
        <List className="menuList sidebar-actions" disablePadding>
            {isSidebarCollapsed && (
                <ListItem disablePadding sx={{justifyContent: 'center', mt: 0, mb: 0}}>
                    {collapseButton}
                </ListItem>
            )}
            <ListItem disablePadding sx={{display: 'flex', alignItems: 'center', mt: 0, mb: 0}}>
                <Tooltip title="New chat" placement="right" disableHoverListener={!isSidebarCollapsed}>
                    <ListItemButton selected={onNewChat} onClick={handleNewChat} sx={collapsedButtonSx}>
                        <ListItemIcon sx={collapsedIconSx}><EditSquareIcon width={18} height={18}/></ListItemIcon>
                        {!isSidebarCollapsed && <ListItemText primary="New chat"/>}
                    </ListItemButton>
                </Tooltip>
                {!isSidebarCollapsed && collapseButton}
            </ListItem>
        </List>

        {/* Session List */}
        <List
            id={SIDEBAR_SESSIONS_SCROLL_ID}
            className="menuList sidebar-items"
            sx={{
                pr: 0.5,
                flex: 1,
                minHeight: 0,
                display: isSidebarCollapsed ? 'none' : undefined,
            }}
        >
            {sessionsPending && sessions.length === 0 ? (
                <Stack sx={{px: 1.5, gap: 1, pt: 1}}>
                    <Skeleton height={28}/>
                    <Skeleton height={28}/>
                    <Skeleton height={28}/>
                    <Skeleton height={28}/>
                </Stack>
            ) : (
                <InfiniteScroll
                    scrollableTarget={SIDEBAR_SESSIONS_SCROLL_ID}
                    dataLength={sessions.length}
                    next={() => {
                        void loadMoreSessions();
                    }}
                    hasMore={hasMoreSessions}
                    loader={
                        <Stack sx={{px: 1.5, gap: 1, py: 1}}>
                            <Skeleton height={28}/>
                            <Skeleton height={28}/>
                        </Stack>
                    }
                    scrollThreshold="80px"
                    style={{overflow: 'visible'}}
                >
                    {groupedSessions.map((group, i) => <React.Fragment key={group.label}>
                        <ListSubheader className={i === 0 ? 'first' : ''} sx={{pl: 1.5, height: '36px', lineHeight: '36px'}}>{group.label}</ListSubheader>
                        {group.sessions.map((session) => (

                            <ListItem sx={{
                                justifyContent: 'space-between',
                                pl: 1.5,
                                borderRadius: '8px',
                                bgcolor: currentSession?.id === session.id ? 'action.selected' : 'transparent',
                                '&:hover': {bgcolor: 'action.hover'}
                            }}
                                      key={session.id} disablePadding
                            >

                                {session.id === renameSessionId ?
                                    <TextField inputRef={renameSessionNameRef} size="small" fullWidth
                                               value={renameSessionName}
                                               onChange={(e) => setRenameSessionName(e.target.value)}
                                               onKeyDown={(e) => {
                                                   if (e.key === 'Enter') handleRenameSessionSubmit(session.appName);
                                                   if (e.key === 'Escape') handleRenameSessionCancel();
                                               }}
                                               onBlur={() => {
                                                   handleRenameSessionSubmit(session.appName);
                                               }}
                                               sx={{
                                                   width: '100%',
                                                   flex: 1,
                                                   fontSize: 14,
                                                   '& .MuiInputBase-input.MuiOutlinedInput-input': {
                                                       paddingLeft: 0,
                                                       fontSize: 14
                                                   },
                                                   '& .MuiOutlinedInput-notchedOutline': {border: 0, paddingLeft: 0},
                                                   '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                                       border: 0,
                                                       paddingLeft: 0
                                                   },
                                                   '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                       border: 0,
                                                       paddingLeft: 0
                                                   },
                                               }}
                                    /> : <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1.5}
                                        onClick={() => selectSession(session)}
                                        sx={{flex: 1, minWidth: 0, cursor: 'pointer', height: '36px !important'}}
                                    >
                                        {session.dashboard
                                            ? <ChartBarSquareIcon color={COLORS.grey[600]} width={18} height={18} style={{flexShrink: 0}}/>
                                            : <ChatSquareIcon color={COLORS.grey[600]} width={18} height={18} style={{flexShrink: 0}}/>}
                                        <Tooltip
                                            title={moment(session.updated_at).format('LLLL')}
                                            placement="right"
                                            arrow
                                        >
                                            <Typography
                                                sx={{
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: 0,
                                                }}
                                                variant="body2"
                                            >
                                                {session.name}
                                            </Typography>
                                        </Tooltip>
                                        {isStreaming(session.id) && (
                                            <CircularProgress size={12} thickness={5} aria-label="Response in progress" sx={{flexShrink: 0}}/>
                                        )}
                                    </Stack>}

                                <ActionsMenu
                                    sessionId={session.id}
                                    sessionName={session.name}
                                    appName={session.appName}
                                    hasDashboard={Boolean(session.dashboard)}
                                    onRenameSession={() => handleSessionIdSelection(session.id, session.name)}
                                />
                            </ListItem>

                        ))}
                    </React.Fragment>)}
                </InfiniteScroll>
            )}
        </List>
    </div>
});
